import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import SiteLogo from './SiteLogo';
import {
  Mail, Phone, MapPin, Youtube, Twitter, Linkedin, Instagram, Facebook,
  ShieldCheck, Zap, CheckCircle, Heart, ChevronDown, ChevronUp
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const normalizeExternalUrl = (value) => {
  if (!value) return '';
  const trimmed = String(value).trim();
  if (!trimmed) return '';
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

const softwareLinks = [
  { label: 'Coaching Institute Software', path: '/coaching-institute-software' },
  { label: 'Coaching Management Software', path: '/coaching-management-software' },
  { label: 'Coaching Center Software', path: '/coaching-center-software' },
  { label: 'Institute Management Software', path: '/institute-management-software' },
  { label: 'Coaching ERP Software', path: '/coaching-erp-software' },
  { label: 'Student Management System', path: '/student-management-system' },
  { label: 'Online Coaching Platform', path: '/online-coaching-platform' },
  { label: 'Coaching Fee Management Software', path: '/coaching-fee-management-software' },
  { label: 'Coaching Institute App', path: '/coaching-institute-app' },
  { label: 'Coaching Software India', path: '/coaching-software-india' },
  { label: 'Coaching Software', path: '/coaching-software' },
  { label: 'Institute Software', path: '/institute-software' },
];

// Shown until the live list loads (or if it cannot): pages that always have institutes
const FALLBACK_SEARCHES = {
  exams: [
    { label: 'JEE Coaching', path: '/jee-coaching' },
    { label: 'NEET Coaching', path: '/neet-coaching' },
    { label: 'UPSC Coaching', path: '/upsc-coaching' },
  ],
  cities: [
    { label: 'Coaching in Gwalior', path: '/coaching-in-gwalior' },
    { label: 'Coaching in Indore', path: '/coaching-in-indore' },
    { label: 'Coaching in Bhopal', path: '/coaching-in-bhopal' },
  ],
};

// Collapsed, each group shows this many links; "View more" shows every page in the group
const COLLAPSED_PER_GROUP = 8;

export default function Footer() {
  const [isExpanded, setIsExpanded] = useState(false);
  const [cmsContent, setCmsContent] = useState(null);
  const [popularCities, setPopularCities] = useState([]);
  const [searchLinks, setSearchLinks] = useState(null);

  useEffect(() => {
    axios.get(`${API}/marketplace/landing-content`)
      .then(r => setCmsContent(r.data))
      .catch(() => {});
    axios.get(`${API}/marketplace/popular-cities`)
      .then(r => setPopularCities(r.data))
      .catch(() => {});
    // City, exam and category pages with institutes on them, the same pages the sitemap lists
    axios.get(`${API}/seo/popular-links`)
      .then(r => setSearchLinks(r.data))
      .catch(() => {});
  }, []);

  const footerConfig = cmsContent?.footer || {};
  const footerBrand = footerConfig.brand_name || 'CoachHub';
  const footerTagline = footerConfig.tagline || cmsContent?.footer_tagline || "Modern ERP and discovery platform for schools, colleges and coaching institutes";
  const footerEmail = footerConfig.email || 'contact@mycoachhub.in';
  const footerPhone = footerConfig.phone || '+91 6260129453';
  const footerCredit = footerConfig.company_credit || 'Nepson Infotech';
  const footerCreditUrl = normalizeExternalUrl(footerConfig.company_credit_url || '');
  const footerSocialLinks = [
    { key: 'youtube', label: 'YouTube', href: normalizeExternalUrl(footerConfig.youtube_url), icon: Youtube },
    { key: 'x', label: 'X', href: normalizeExternalUrl(footerConfig.x_url), icon: Twitter },
    { key: 'linkedin', label: 'LinkedIn', href: normalizeExternalUrl(footerConfig.linkedin_url), icon: Linkedin },
    { key: 'instagram', label: 'Instagram', href: normalizeExternalUrl(footerConfig.instagram_url), icon: Instagram },
    { key: 'facebook', label: 'Facebook', href: normalizeExternalUrl(footerConfig.facebook_url), icon: Facebook },
  ].filter((link) => link.href);

  const footerCities = popularCities.slice(0, 10);
  const searchGroups = [
    { title: 'Coaching by city', links: searchLinks?.cities || FALLBACK_SEARCHES.cities },
    { title: 'Exam coaching', links: searchLinks?.exams || FALLBACK_SEARCHES.exams },
    { title: 'Exam coaching by city', links: searchLinks?.exam_cities || [] },
    { title: 'Categories', links: searchLinks?.categories || [] },
    { title: 'Institute software', links: softwareLinks },
  ].filter((group) => group.links.length > 0);
  const hasMore = searchGroups.some((group) => group.links.length > COLLAPSED_PER_GROUP);

  return (
    <footer className="relative bg-brand-night text-white pt-14 pb-6 px-4 border-t-4 border-brand-gold" data-testid="landing-footer">
      <div className="max-w-7xl mx-auto">
        {/* Popular SEO Searches */}
        <div className="border-b border-white/10 pb-4 mb-6 text-xs">
          <p className="text-white/60 font-semibold mb-3 tracking-wider uppercase">Popular Searches</p>
          <div className="space-y-2.5" data-testid="footer-popular-searches">
            {searchGroups.map((group) => (
              <div key={group.title} className="flex flex-col gap-1 sm:flex-row sm:gap-3">
                <p className="shrink-0 font-semibold text-white/55 sm:w-40">{group.title}</p>
                <div className="flex flex-wrap gap-x-3 gap-y-1.5 text-white/45">
                  {(isExpanded ? group.links : group.links.slice(0, COLLAPSED_PER_GROUP)).map((link, idx) => (
                    <React.Fragment key={link.path}>
                      {idx > 0 && <span className="text-white/20" aria-hidden="true">|</span>}
                      <Link to={link.path} className="hover:text-brand-gold/80 transition-colors">{link.label}</Link>
                    </React.Fragment>
                  ))}
                </div>
              </div>
            ))}
          </div>
          {hasMore && (
            <button
              type="button"
              onClick={() => setIsExpanded(!isExpanded)}
              aria-expanded={isExpanded}
              className="mt-3 text-xs text-brand-gold hover:text-brand-gold-soft font-medium inline-flex items-center gap-1 transition-colors"
              data-testid="footer-searches-toggle"
            >
              <span>{isExpanded ? 'View Less' : 'View More'}</span>
              {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-12 gap-x-6 gap-y-10 mb-12">
          {/* Brand */}
          <div className="col-span-2 md:col-span-4 lg:col-span-4">
            <Link to="/" className="inline-flex mb-5" aria-label="mycoachhub.in home">
              <SiteLogo variant="dark" />
            </Link>
            <p className="text-white/60 text-sm leading-relaxed mb-5 max-w-sm">{footerTagline}</p>

            <ul className="space-y-2 mb-5">
              <li>
                <a href={`mailto:${footerEmail}`} className="flex items-center gap-2 text-sm text-white/80 hover:text-brand-gold transition-colors" data-testid="footer-email">
                  <Mail className="w-4 h-4 flex-shrink-0 text-brand-gold" /> {footerEmail}
                </a>
              </li>
              <li>
                <a href={`tel:${footerPhone.replace(/\s/g, '')}`} className="flex items-center gap-2 text-sm text-white/80 hover:text-brand-gold transition-colors" data-testid="footer-phone">
                  <Phone className="w-4 h-4 flex-shrink-0 text-brand-gold" /> {footerPhone}
                </a>
              </li>
              <li className="flex items-start gap-2 text-sm text-white/60">
                <MapPin className="w-4 h-4 flex-shrink-0 text-brand-gold mt-0.5" />
                <span>Gwalior, Madhya Pradesh, India</span>
              </li>
            </ul>

            {footerSocialLinks.length > 0 && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-white/45 mb-3">Follow Us</p>
                <div className="flex flex-wrap gap-2">
                  {footerSocialLinks.map(({ key, label, href, icon: Icon }) => (
                    <a
                      key={key}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer me"
                      className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-white/15 text-white/70 hover:border-brand-gold hover:text-brand-gold hover:bg-brand-gold/10 transition-colors"
                      aria-label={`Visit CoachHub on ${label}`}
                      data-testid={`footer-social-${key}`}
                    >
                      <Icon className="w-4 h-4" />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Product */}
          <div className="lg:col-span-2">
            <p className="text-xs font-bold text-brand-gold mb-4 uppercase tracking-[0.2em]">Product</p>
            <ul className="space-y-2.5">
              <li><Link to="/features" className="text-sm text-white/60 hover:text-brand-gold transition-colors">Features</Link></li>
              <li><Link to="/#erp-types" className="text-sm text-white/60 hover:text-brand-gold transition-colors">ERP Types</Link></li>
              <li><Link to="/#pricing" className="text-sm text-white/60 hover:text-brand-gold transition-colors">Pricing</Link></li>
              <li><Link to="/#demo" className="text-sm text-white/60 hover:text-brand-gold transition-colors">Book a Demo</Link></li>
              <li><Link to="/institutes" className="text-sm text-white/60 hover:text-brand-gold transition-colors">All Institutes</Link></li>
              <li><Link to="/register" className="text-sm text-white/60 hover:text-brand-gold transition-colors">List Your Institute</Link></li>
              <li><Link to="/login" className="text-sm text-white/60 hover:text-brand-gold transition-colors">Login</Link></li>
            </ul>
          </div>

          {/* Company */}
          <div className="lg:col-span-2">
            <p className="text-xs font-bold text-brand-gold mb-4 uppercase tracking-[0.2em]">Company</p>
            <ul className="space-y-2.5">
              <li><Link to="/about" className="text-sm text-white/60 hover:text-brand-gold transition-colors">About Us</Link></li>
              <li><Link to="/become-a-partner" className="text-sm text-white/60 hover:text-brand-gold transition-colors">Become a Partner</Link></li>
              <li><Link to="/#contact" className="text-sm text-white/60 hover:text-brand-gold transition-colors">Contact Us</Link></li>
              <li><Link to="/page/support" className="text-sm text-white/60 hover:text-brand-gold transition-colors">Help & Support</Link></li>
              <li><Link to="/#faq" className="text-sm text-white/60 hover:text-brand-gold transition-colors">FAQ</Link></li>
            </ul>
          </div>

          {/* Legal */}
          <div className="lg:col-span-2">
            <p className="text-xs font-bold text-brand-gold mb-4 uppercase tracking-[0.2em]">Legal</p>
            <ul className="space-y-2.5">
              <li><Link to="/page/terms-and-condition" className="text-sm text-white/60 hover:text-brand-gold transition-colors" data-testid="footer-terms">Terms & Conditions</Link></li>
              <li><Link to="/page/privacy-policy" className="text-sm text-white/60 hover:text-brand-gold transition-colors" data-testid="footer-privacy">Privacy Policy</Link></li>
              <li><Link to="/page/return-cancellation-policy" className="text-sm text-white/60 hover:text-brand-gold transition-colors" data-testid="footer-refund">Refund Policy</Link></li>
            </ul>
          </div>

          {/* Features — always present so the last column is never empty */}
          <div className="lg:col-span-2">
            <p className="text-xs font-bold text-brand-gold mb-4 uppercase tracking-[0.2em]">Features</p>
            <ul className="space-y-2.5">
              {[
                { label: 'Biometric Attendance', to: '/#attendance' },
                { label: 'Fee Management', to: '/#fees' },
                { label: 'AI Lead CRM', to: '/#crm' },
                { label: 'WhatsApp Integration', to: '/#whatsapp' },
                { label: 'Multi-branch ERP', to: '/#multi-branch' },
              ].map((item) => (
                <li key={item.to}><Link to={item.to} className="text-sm text-white/60 hover:text-brand-gold transition-colors">{item.label}</Link></li>
              ))}
            </ul>
          </div>

          {/* Popular cities — a wrapped row so it never leaves a gap when the list is short or empty */}
          {footerCities.length > 0 && (
            <div className="col-span-full">
              <p className="text-xs font-bold text-brand-gold mb-3 uppercase tracking-[0.2em]">Popular Cities</p>
              <div className="flex flex-wrap gap-2">
                {footerCities.map((cityItem) => (
                  <Link
                    key={cityItem.slug || cityItem.city}
                    to={`/coaching-in-${cityItem.slug || cityItem.city.toLowerCase().replace(/\s+/g, '-')}`}
                    className="rounded-full border border-white/10 px-3 py-1.5 text-xs text-white/60 hover:border-brand-gold hover:text-brand-gold transition-colors"
                  >
                    {cityItem.city}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Trust strip */}
        <div className="border-t border-white/10 pt-6 mb-6 flex flex-wrap items-center justify-center md:justify-between gap-4 text-white/45 text-xs">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-emerald-500" /> Role-based access</span>
            <span className="inline-flex items-center gap-1.5"><CheckCircle className="w-4 h-4 text-emerald-500" /> Audit log for fees & marks</span>
            <span className="inline-flex items-center gap-1.5"><Zap className="w-4 h-4 text-brand-gold" /> Free data migration</span>
            <span className="inline-flex items-center gap-1.5"><Heart className="w-4 h-4 text-rose-400" /> Made in India</span>
          </div>
          <span>Support: Mon – Sat, 10:00 AM – 7:00 PM</span>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-white/10 pt-6 flex flex-col md:flex-row items-center justify-between gap-3">
          <p className="text-white/45 text-sm">&copy; {new Date().getFullYear()} {footerBrand}. All rights reserved.</p>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-white/45">
            <Link to="/page/terms-and-condition" className="hover:text-brand-gold/80 transition-colors">Terms</Link>
            <Link to="/page/privacy-policy" className="hover:text-brand-gold/80 transition-colors">Privacy</Link>
            <Link to="/page/return-cancellation-policy" className="hover:text-brand-gold/80 transition-colors">Refunds</Link>
            <Link to="/page/contact-us" className="hover:text-brand-gold/80 transition-colors">Contact</Link>
          </div>
          <p className="text-white/40 text-xs">
            Product by{' '}
            {footerCreditUrl ? (
              <a href={footerCreditUrl} target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-brand-gold transition-colors font-medium" data-testid="footer-credit-link">
                {footerCredit}
              </a>
            ) : (
              <span className="text-white/80 font-medium" data-testid="footer-credit">{footerCredit}</span>
            )}
          </p>
        </div>
      </div>
    </footer>
  );
}
